import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;
// Prevent brief pointer crossings from replacing the product panel beneath a pending click.
const CATALOG_ACTIVATE_DELAY = 160;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('pointerover', this.#handleCatalogIntent, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('pointerout', this.#handleCatalogIntentExit, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('focusin', this.#handleCatalogIntent, {
      signal: this.#abortController.signal,
    });

    onDocumentReady(this.#preloadImages);
    onDocumentReady(this.#initializeServiceAnchors);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearCatalogIntent();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #catalogIntentTimer = 0;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.#updateHoverBridge();

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu instanceof HTMLElement) {
      this.#positionSimpleSubmenu(submenu);
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.matches(':hover') || this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #initializeServiceAnchors = () => {
    if (window.location.pathname !== '/pages/services') return;

    const serviceAnchors = new Map([
      ['Hyperbaric Oxygen Therapy', 'hyperbaric-oxygen-therapy'],
      ['Red Light Therapy', 'red-light-therapy'],
      ['Acoustic Therapy', 'acoustic-therapy'],
      ['Contrast Therapy', 'contrast-therapy'],
      ['PEMF Therapy', 'pemf-therapy'],
      ['Peptide Therapy', 'peptide-therapy'],
      ['Aesthetic Services Powered by InMode', 'aesthetic-services'],
    ]);

    document.querySelectorAll('main h4, main h5').forEach((heading) => {
      const anchor = serviceAnchors.get(heading.textContent?.trim() ?? '');
      if (!anchor) return;

      const headerHeight = document.querySelector('header-component')?.offsetHeight ?? 80;
      heading.id = anchor;
      heading.style.scrollMarginTop = `${headerHeight + 24}px`;
    });

    if (!window.location.hash) return;

    const target = document.getElementById(window.location.hash.slice(1));
    if (!(target instanceof HTMLElement)) return;

    window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
  };

  #positionSimpleSubmenu = (submenu) => {
    if (!submenu.classList.contains('menu-list__submenu--simple')) return;

    const header = this.closest('header-component');
    const offsetParent = submenu.offsetParent;

    if (!(header instanceof HTMLElement) || !(offsetParent instanceof HTMLElement)) return;

    const headerBottom = header.getBoundingClientRect().bottom;
    const offsetParentTop = offsetParent.getBoundingClientRect().top;
    const submenuTop = Math.max(0, headerBottom - offsetParentTop);

    submenu.style.setProperty('--simple-submenu-top', `${submenuTop}px`);
  };

  #updateHoverBridge = () => {
    const header = this.closest('header-component');
    if (!(header instanceof HTMLElement)) return;

    const currentBridge = parseFloat(
      getComputedStyle(this).getPropertyValue('--header-menu-hover-bridge') || '0'
    );
    const baseMenuBottom = this.getBoundingClientRect().bottom - currentBridge;
    const headerBottom = header.getBoundingClientRect().bottom;
    const nextBridge = Math.max(0, headerBottom - baseMenuBottom);

    this.style.setProperty('--header-menu-hover-bridge', `${nextBridge}px`);
  };

  #handleCatalogIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const serviceTrigger = event.target.closest('[data-service-trigger]');
    if (serviceTrigger instanceof HTMLElement) {
      const menu = serviceTrigger.closest('[data-menu-layout="services-catalog"]');
      const key = serviceTrigger.dataset.serviceTrigger;

      if (!(menu instanceof HTMLElement) || !key) return;

      if (event instanceof FocusEvent) {
        this.#clearCatalogIntent();
        this.#setActiveService(menu, key);
        return;
      }

      if (event.relatedTarget instanceof Node && serviceTrigger.contains(event.relatedTarget)) return;

      this.#clearCatalogIntent();
      this.#catalogIntentTimer = window.setTimeout(() => {
        this.#catalogIntentTimer = 0;

        if (serviceTrigger.matches(':hover')) {
          this.#setActiveService(menu, key);
        }
      }, CATALOG_ACTIVATE_DELAY);
      return;
    }

    const productLink = event.target.closest('[data-catalog-product-category]');
    if (productLink instanceof HTMLElement) {
      this.#clearCatalogIntent();

      const menu = productLink.closest('[data-menu-layout="collection-catalog"]');
      const categoryKey = productLink.dataset.catalogProductCategory;

      if (!(menu instanceof HTMLElement) || !categoryKey) return;

      this.#updateCatalogProductPreview(menu, categoryKey, productLink);
      return;
    }

    const trigger = event.target.closest('[data-catalog-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    const menu = trigger.closest('[data-menu-layout="collection-catalog"]');
    const key = trigger.dataset.catalogTrigger;

    if (!(menu instanceof HTMLElement) || !key) return;

    if (event instanceof FocusEvent) {
      this.#clearCatalogIntent();
      this.#setActiveCatalogCategory(menu, key);
      return;
    }

    if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;

    this.#clearCatalogIntent();
    this.#catalogIntentTimer = window.setTimeout(() => {
      this.#catalogIntentTimer = 0;

      if (trigger.matches(':hover')) {
        this.#setActiveCatalogCategory(menu, key);
      }
    }, CATALOG_ACTIVATE_DELAY);
  };

  #handleCatalogIntentExit = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-catalog-trigger], [data-service-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;

    this.#clearCatalogIntent();
  };

  #clearCatalogIntent = () => {
    if (!this.#catalogIntentTimer) return;

    window.clearTimeout(this.#catalogIntentTimer);
    this.#catalogIntentTimer = 0;
  };

  #setActiveCatalogCategory(menu, key) {
    menu.querySelectorAll('[data-catalog-trigger]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.catalogTrigger === key);
    });

    menu.querySelectorAll('[data-catalog-panel]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.catalogPanel === key);
    });

    menu.querySelectorAll('[data-catalog-media]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.catalogMedia === key);
    });

    this.#setActiveCatalogProduct(menu, key);
    this.#resetCatalogMedia(menu, key);
  }

  #setActiveService(menu, key) {
    menu.querySelectorAll('[data-service-trigger]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.serviceTrigger === key);
    });

    menu.querySelectorAll('[data-service-panel]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.servicePanel === key);
    });
  }

  #updateCatalogProductPreview(menu, key, productLink) {
    const panel = menu.querySelector(`[data-catalog-media="${key}"]`);
    if (!(panel instanceof HTMLElement)) return;

    this.#setActiveCatalogProduct(menu, key, productLink);

    const image = panel.querySelector('[data-catalog-image-element]');
    if (!(image instanceof HTMLImageElement)) return;

    const nextSrc = productLink.dataset.catalogProductImage;
    const nextAlt = productLink.dataset.catalogProductAlt;

    if (nextSrc) {
      image.src = nextSrc;
    }

    if (nextAlt) {
      image.alt = nextAlt;
    }
  }

  #setActiveCatalogProduct(menu, key, activeLink = null) {
    menu.querySelectorAll('[data-catalog-product-category]').forEach((element) => {
      const isSameCategory = element.dataset.catalogProductCategory === key;
      const isActive =
        activeLink instanceof HTMLElement
          ? element === activeLink
          : isSameCategory &&
            element ===
              menu.querySelector(`[data-catalog-panel="${key}"] [data-catalog-product-category="${key}"]`);

      element.classList.toggle('is-active', isActive);
    });
  }

  #resetCatalogMedia(menu, key) {
    const panel = menu.querySelector(`[data-catalog-media="${key}"]`);
    if (!(panel instanceof HTMLElement)) return;

    const image = panel.querySelector('[data-catalog-image-element]');
    if (!(image instanceof HTMLImageElement)) return;

    const defaultSrc = image.dataset.catalogDefaultSrc;
    const defaultAlt = image.dataset.catalogDefaultAlt;

    if (defaultSrc) {
      image.src = defaultSrc;
    }

    if (defaultAlt) {
      image.alt = defaultAlt;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
